/**
 * Format currency
 */
export function formatCurrency(
  amount: number,
  currency?: string,
  showCode: boolean = false
): string {
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'CAD',
  }).format(amount)

  return showCode && currency ? `${formatted} ${currency}` : formatted
}

/**
 * Format price (alias for formatCurrency)
 */
export function formatPrice(amount: number, currency?: string, showCode: boolean = true): string {
  // Handle invalid numbers
  const validAmount = isNaN(amount) || amount === null || amount === undefined ? 0 : amount
  return formatCurrency(validAmount, currency, showCode && !!currency)
}

/**
 * Format phone number
 */
export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`
  }
  return phone
}

/**
 * Format date and time
 */
export function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

/**
 * Format date only
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

/**
 * Format time only
 */
export function formatTime(dateString: string): string {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

/**
 * Get relative time (e.g., "in 30 minutes", "2 hours ago")
 */
export function getRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInMs = date.getTime() - now.getTime()
  const diffInMinutes = Math.round(diffInMs / 60000)

  if (diffInMinutes < 0) {
    // Past
    const absDiff = Math.abs(diffInMinutes)
    if (absDiff < 60) return `${absDiff} minute${absDiff !== 1 ? 's' : ''} ago`
    const hours = Math.round(absDiff / 60)
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`
  } else {
    // Future
    if (diffInMinutes < 60)
      return `in ${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''}`
    const hours = Math.round(diffInMinutes / 60)
    return `in ${hours} hour${hours !== 1 ? 's' : ''}`
  }
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 3) + '...'
}

/**
 * Capitalize first letter
 */
export function capitalize(text: string): string {
  if (!text) return ''
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

/**
 * Format order status for display
 */
export function formatOrderStatus(status: string): string {
  return status
    .split('-')
    .map((word) => capitalize(word))
    .join(' ')
}
